import { useMemo, useState } from 'react';
import { useOrgFilter } from '@/store/defaults';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { employees, EMPLOYMENT_TYPE_LABELS, type EmployeeFull, type EmploymentType } from '@/api/hr';
import { orgs } from '@/api/catalogs';
import type { ColDef } from 'ag-grid-community';
import ImportedBadge from '@/shared/components/ImportedBadge';
import InfiniteDocGrid, { TitleTotal } from '@/shared/components/InfiniteDocGrid';
import { useInfiniteGridDatasource } from '@/hooks/useInfiniteGridDatasource';

// Фільтр «чинний»: усі / лише чинні / лише не чинні.
type ActiveFilter = 'all' | 'active' | 'inactive';

export default function EmployeesPage() {
  const navigate = useNavigate();
  const [activeFilter, setActiveFilter] = useState<ActiveFilter>('all');
  const [q, setQ] = useState('');
  // Орг за замовчуванням з шапки (override при ручному виборі).
  const [orgId, setOrgId] = useOrgFilter();
  const [empType, setEmpType] = useState('');  // '' = усі види договору
  const orgList = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });

  // orgId → назва (колонка «Організація»).
  const orgName = useMemo(() => {
    const m = new Map<string, string>();
    for (const o of orgList.data?.items ?? []) m.set(o.id, o.name);
    return m;
  }, [orgList.data]);

  // 2026-08-23 (Норма #29): серверна пагінація + сортування; фільтр
  // «не чинні» — на сервері (inactive_only), а не клієнтський filter.
  const { datasource, total } = useInfiniteGridDatasource<EmployeeFull>(
    (limit, offset, sort) => employees.list({
      organization_id: orgId || undefined,
      active_only: activeFilter === 'active',
      inactive_only: activeFilter === 'inactive',
      employment_type: empType || undefined,
      q: q || undefined,
      sort_by: sort?.sort_by, sort_dir: sort?.sort_dir,
      limit, offset,
    }),
    [activeFilter, q, orgId, empType],
    { attachmentsType: 'ref.employees' },
  );

  const cols = useMemo<ColDef<EmployeeFull>[]>(() => [
    {
      field: 'is_active', headerName: 'Чинний', width: 80,
      cellStyle: { textAlign: 'center' },
      cellRenderer: (p: { data?: EmployeeFull }) => !p.data ? '' : p.data.is_active
        ? <span title="Чинний" style={{ color: '#16a34a', fontWeight: 700 }}>✓</span>
        : <span title="Не чинний" style={{ color: '#94a3b8' }}>—</span>,
    },
    {
      headerName: '', width: 44, sortable: false,
      cellRenderer: (p: { data?: EmployeeFull }) =>
        <ImportedBadge source={p.data?.external_source} />,
    },
    { field: 'code', headerName: 'Таб.№', width: 110 },
    { field: 'full_name', headerName: 'ПІБ', flex: 1, minWidth: 220 },
    { field: 'inn', headerName: 'ІПН', width: 130 },
    {
      field: 'employment_type', headerName: 'Вид договору', width: 170,
      valueFormatter: (p) => p.data ? (EMPLOYMENT_TYPE_LABELS[p.value as EmploymentType] ?? p.value ?? '—') : '',
    },
    {
      field: 'organization_id', headerName: 'Організація', width: 220,
      valueFormatter: (p) => p.value ? (orgName.get(p.value) ?? '—') : '',
    },
    {
      colId: '_att', headerName: 'Вкладення', width: 110, sortable: false,
      cellStyle: { textAlign: 'center' },
      cellRenderer: (p: { data?: { _att?: boolean } }) => p.data?._att ? '✓' : '',
    },
  ], [orgName]);

  const selStyle = { width: 150, flex: '0 0 auto' } as const;

  return (
    <div className="card">
      <div className="toolbar with-filters" style={{ gap: '0.4rem', flexWrap: 'wrap' }}>
        <h2 style={{ margin: 0 }}>Співробітники<TitleTotal total={total} /></h2>
        <button className="primary" style={{ marginLeft: 'auto' }}
                onClick={() => navigate('/employees/new-unified')}>
          + Новий
        </button>
      </div>
      <div className="filters">
        <select value={orgId} onChange={(e) => setOrgId(e.target.value)} style={{ maxWidth: 260 }}>
          <option value="">Усі організації</option>
          {orgList.data?.items.map((o) => (
            <option key={o.id} value={o.id}>{o.name} — {o.code}</option>
          ))}
        </select>
        <select value={empType} onChange={(e) => setEmpType(e.target.value)} style={selStyle}>
          <option value="">Усі види договору</option>
          {(Object.entries(EMPLOYMENT_TYPE_LABELS) as Array<[EmploymentType, string]>)
            .filter(([v]) => v !== 'NO_CONTRACT')
            .map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <select value={activeFilter}
                onChange={(e) => setActiveFilter(e.target.value as ActiveFilter)}
                style={selStyle}>
          <option value="all">Усі</option>
          <option value="active">Лише чинні</option>
          <option value="inactive">Лише не чинні</option>
        </select>
        <input placeholder="ПІБ або ІПН…" value={q}
               onChange={(e) => setQ(e.target.value)}
               style={{ width: 180, flex: '0 0 auto' }} />
      </div>
      <InfiniteDocGrid<EmployeeFull>
        datasource={datasource}
        columnDefs={cols}
        storageKey="employees-list"
        onRowDoubleClicked={(e) => navigate(`/employees/${e.id}`)}
        getRowStyle={(p) => p.data?.deletion_mark ? { opacity: 0.4 } : (p.data && !p.data.is_active ? { opacity: 0.6 } : undefined)}
      />
    </div>
  );
}
